import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import {certificatesState, clientActions} from "redux/clientSlice"
import {vendorActions, vendorState} from "redux/vendorSlice";
import {organizationState} from "redux/organizationSlice";
import {loggedInUserState} from "redux/userSlice";
import { Box, IconButton, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import CertificateTable from "components/CertificateTable";
import EmailConfirmationModal from "components/VendorDetails/EmailConfirmationModal";
import {getNavigationPath, useNavPath, NAVIGATED_FROM} from "constants/routes";
import {client_mode} from "constants/client_properties";
import { getTableRowsPerPage, getProjectLabel } from "constants/organization_types";
import BoxShadow from "components/shared/BoxShadow";
import Snackbar from "components/shared/Snackbar";
import certificate_actions from "constants/certificate_actions";
import TourOutline from "components/shared/TourOutline";
import { useEntityTour } from "hooks/useTour";
import { CERTIFICATES_TOUR, DEFAULT_TOUR_STYLES, TOUR_TITLES, CustomTooltip } from "config/tours";

export default () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userOrgType, selectedClient } = useSelector(organizationState)
  const { loggedInUser } = useSelector(loggedInUserState);
  const defaultTableRowsPerPage = getTableRowsPerPage(selectedClient);
  const {certificates, certificatesCount, paginatedCertificates, certificateStatusCounts, certificateStatusCountsLoading} = useSelector(certificatesState)
  const isProjectBased = selectedClient?.mode === client_mode.project
  const projects = useSelector(state => state.client.projects[selectedClient?.clientID]) || []
  const { vendors } = useSelector(vendorState)
  const NAV_PATH = useNavPath(userOrgType)
  const [initialLoading, setInitialLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [nextTokens, setNextTokens] = useState([null])
  const [emailConfirm, setEmailConfirm] = useState(false)
  const [emailCertificateId, setEmailCertificateId] = useState()
  const [emailTemplates, setEmailTemplates] = useState(["Custom"])
  const [message, setMessage] = useState()
  const serverSide = true;

  // Tour state
  const [tourKey, setTourKey] = useState(0);
  const {
    isRunning: isTourRunning,
    stepIndex: tourStepIndex,
    completedSteps,
    startTour,
    stopTour,
    goToStep,
  } = useEntityTour(CERTIFICATES_TOUR.id, selectedClient?.clientID);

  useEffect(() => {
    if(selectedClient?.clientID) {
      loadInitialData()
    }
  }, [selectedClient?.clientID])

  async function loadInitialData() {
    setInitialLoading(true)
    // Always fetch vendors to ensure we have the latest data
    await dispatch(vendorActions.fetchVendors(selectedClient.clientID))
    const initialPaginatedCerts = await dispatch(clientActions.getPaginatedClientCertificates({
      clientID: selectedClient?.clientID,
      sortBy: "lastModifiedDate",
      sortAsc: false,
      nextToken: null,
      rowsPerPage: serverSide ? defaultTableRowsPerPage : undefined,
    }))
    setTokenNextPage(initialPaginatedCerts?.payload?.nextToken,1)
    
    // Load all certificates for search functionality (even with server-side pagination)
    dispatch(clientActions.getAllClientCertificates(selectedClient.clientID))
    
    // fetch certificate counts by status
    dispatch(clientActions.getCertificateCountsByStatus(selectedClient.clientID))
    setInitialLoading(false)
  }

  const setTokenNextPage = (nextToken, page) => {
    const newNextTokens = [...nextTokens]
    if (nextToken) {
      newNextTokens[page] = nextToken || null
    }
    setNextTokens(newNextTokens)
  }

  async function onFetchData({
    sortBy,
    sortAsc,
    page,
    rowsPerPage,
    status,
    statusFilter,
  }) {
    if(page === 0) {
      await setNextTokens([null])
    }
    if(selectedClient?.clientID) {
      setLoading(true)
      const certResult = await dispatch(clientActions.getPaginatedClientCertificates({
        clientID: selectedClient?.clientID,
        sortBy,
        sortAsc,
        nextToken: nextTokens[page],
        rowsPerPage,
        status,
        statusFilter
      }))
      setTokenNextPage(certResult?.payload?.nextToken, page+1)
      setLoading(false)
    }
  }

  const projectMap = useMemo(() => {
    const mapToReturn = {}
    if(isProjectBased && projects?.length) {
      projects.forEach(project => {
        mapToReturn[project.id] = project.name
      })
    }
    return mapToReturn
  }, [projects, isProjectBased])

  function navToCertificate(id) {
    const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
      clientId: selectedClient.id,
      vendorId: NAVIGATED_FROM.CERTIFICATES,
      certificateId: id
    })
    navigate(certificatePath, {
      state: {
        referrer: location.pathname
      }
    })
  }

  function onEmailSentResult(success) {
    if (success) {
      setMessage({
        text: "Successfully sent email notification.",
        severity: "success",
      });
    } else {
      setMessage({
        text: "Unable to send email notification.",
        severity: "error",
      });
    }
  }

  function onSendCertificateEmail(certificateId, emailTemplate) {
    console.log('onSendCertificateEmail called:', { certificateId, emailTemplate });
    setEmailCertificateId(certificateId);
    setEmailTemplates([emailTemplate]);
    setEmailConfirm(true);
    
    // Debug vendors data
    console.log('Vendors:', vendors);
    console.log('Certificates:', certificates);
  }

  // Generate upload link for sharing existing certificates
  async function getUploadLink(certificateId, projectID, mode) {
    const certificate = certificates.find(cert => cert.id === certificateId);
    if (!certificate) {
      console.error('Certificate not found:', certificateId);
      return null;
    }
    
    const vendor = vendors.find(v => v.id === certificate.clientVendorID);
    if (!vendor) {
      console.error('Vendor not found for certificate:', certificate.clientVendorID);
      return null;
    }
    
    // Handle both vendor.vendorID and vendor.vendor.id structures
    const vendorId = vendor.vendorID || vendor.vendor?.id;
    if (!vendorId) {
      console.error('Vendor ID not found in vendor object:', vendor);
      return null;
    }
    
    return window.location.origin + `/vendor/upload/${selectedClient.id}/${vendorId}/${certificateId}${mode ? `/${mode}` : ""}`;
  }
  const columns = [
    "id",
    "certificateType",
    "status",
    "vendor",
    "subcontractorStatus",
    "producer",
    "certificateDate",
    "lastModifiedDate",
    "createdDate",
    "policyExp"
  ]
  if(isProjectBased) {
    columns.push("project")
  }
  columns.push("scan")
  columns.push("actions")

  // Find the certificate and vendor info for email modal
  const selectedCertificate = useMemo(() => {
    if (!emailCertificateId || !certificates) return null;
    return certificates.find(cert => cert.id === emailCertificateId);
  }, [emailCertificateId, certificates]);

  const selectedVendor = useMemo(() => {
    if (!selectedCertificate || !vendors) return null;
    return vendors.find(v => v.id === selectedCertificate.clientVendorID);
  }, [selectedCertificate, vendors]);

  // Tour callback
  const handleTourCallback = useCallback((data) => {
    const { action, status, type, index } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      if (action === ACTIONS.NEXT) {
        goToStep(index + 1);
      } else if (action === ACTIONS.PREV) {
        goToStep(index - 1);
      }
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
    }

    if (action === ACTIONS.CLOSE) {
      stopTour();
    }
  }, [goToStep, stopTour]);

  // Start tour handler
  const handleStartTour = useCallback(() => {
    setTourKey(prev => prev + 1);
    setTimeout(() => {
      startTour();
    }, 100);
  }, [startTour]);

  // Handle clicking on steps in the tour outline
  const handleStepClick = useCallback((index) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        goToStep(index);
      }, 50);
    });
  }, [goToStep]);

  return (
    <>
      {/* Tour Progress Sidebar */}
      {isTourRunning && (
        <TourOutline
          title={TOUR_TITLES[CERTIFICATES_TOUR.id]}
          steps={CERTIFICATES_TOUR.steps}
          currentStep={tourStepIndex}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          onClose={stopTour}
        />
      )}

      {/* Joyride Tour */}
      <Joyride
        key={tourKey}
        steps={CERTIFICATES_TOUR.steps}
        stepIndex={tourStepIndex}
        run={isTourRunning}
        continuous
        showSkipButton
        showProgress
        disableScrolling={false}
        disableScrollParentFix
        spotlightClicks
        callback={handleTourCallback}
        tooltipComponent={CustomTooltip}
        styles={DEFAULT_TOUR_STYLES}
      />

      <BoxShadow sx={{p:0}} className="certificates-container">
        {/* Help button to start tour */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Tooltip title="Start guided tour">
            <IconButton onClick={handleStartTour} size="small">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <CertificateTable
          loading={initialLoading || loading}
          certificates={[...(certificates || [])]}
          paginatedCertificates={paginatedCertificates}
          columns={columns}
          onView={navToCertificate}
          onSendEmail={onSendCertificateEmail}
          projects={projectMap}
          serverSide={serverSide}
          onFetchData={onFetchData}
          certificatesCount={certificatesCount}
          certificateStatusCounts={certificateStatusCounts}
          certificateStatusCountsLoading={certificateStatusCountsLoading}
          nextTokens={nextTokens}
          setNextTokens={setNextTokens}
          subcontractors={vendors}
        />
      </BoxShadow>
      
      {selectedVendor && (
        <EmailConfirmationModal
          open={emailConfirm}
          onClose={() => setEmailConfirm(false)}
          clientName={selectedClient?.name}
          clientId={selectedClient?.clientID}
          clientLogo={selectedClient?.logo}
          vendorName={selectedVendor?.vendor?.name}
          clientVendorID={selectedVendor?.id}
          vendorEmail={selectedVendor?.vendor?.email || ''}
          templates={emailTemplates}
          clientTemplates={selectedClient?.templates}
          onEmailSentResult={onEmailSentResult}
          certificateId={emailCertificateId}
          getUploadLink={getUploadLink}
          vendorProjects={isProjectBased ? projects : []}
          defaultProject={projects?.find((project) => project.isDefault)}
          loggedInUser={loggedInUser}
          useClientDefaultProject={selectedClient?.useDefaultProject}
          additionalRecipients={[
            ...(selectedClient?.usePrimaryNotificationEmailAddress ? [selectedClient?.primaryNotificationEmailAddress] : []), 
            ...(Array.isArray(selectedVendor?.vendor?.additionalEmails) ? selectedVendor.vendor.additionalEmails : [])
          ]}
          projectLabel={getProjectLabel(selectedClient)}
        />
      )}
      
      <Snackbar
        open={!!message}
        onClose={() => setMessage(undefined)}
        message={message?.text}
        severity={message?.severity}
      />
    </>
  )
}
